import { useCallback, useEffect, useState } from 'react';

import { hrApi } from '@/api';

import { EmployeeSelect, ExpenseTypeSelect } from '@/components/selects';

import { PageHeader } from '@/components/layout/PageHeader';

import { Button } from '@/components/ui/Button';

import { DataTable, type Column, TableRowActions } from '@/components/ui/DataTable';

import { dataTablePageProps, paginateClientList, usePagination } from '@/hooks/usePagination';

import { Input } from '@/components/ui/Input';

import { DatePicker } from '@/components/ui/DatePicker';

import { FormGrid } from '@/components/ui/FormGrid';

import { FormActions } from '@/components/ui/FormActions';

import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';

import type { HrExpenseClaim } from '@/types/hr';

import { showApiError } from '@/utils/apiError';

import { notify } from '@/core/toast/notify';



export function HrClaimsPage() {

  const pagination = usePagination();

  const { getLabel } = useEmployeeOptions({ hrScoped: true });

  const [data, setData] = useState<HrExpenseClaim[]>([]);

  const [loading, setLoading] = useState(false);

  const [employeeId, setEmployeeId] = useState('');

  const [claimDate, setClaimDate] = useState('');

  const [totalAmount, setTotalAmount] = useState('');

  const [expenseTypeCode, setExpenseTypeCode] = useState('');

  const [creating, setCreating] = useState(false);



  const load = useCallback(async () => {

    setLoading(true);

    try {

      const rows = await hrApi.listExpenses();

      setData(rows);

      pagination.applyClientCount(rows.length);

    } catch (err) {

      showApiError(err, 'Failed to load expense claims');

    } finally {

      setLoading(false);

    }

  }, []);



  useEffect(() => {

    load();

  }, [load]);



  const createClaim = async () => {

    if (!employeeId.trim() || !claimDate || !totalAmount.trim() || !expenseTypeCode) {

      notify.error('Employee, expense type, date, and amount are required');

      return;

    }

    setCreating(true);

    try {

      await hrApi.createExpense({

        employeeId: Number(employeeId),

        claimDate,

        totalAmount: Number(totalAmount),

        currencyCode: 'INR',

        expenseTypeCode,

      });

      notify.success('Expense claim created');

      setEmployeeId('');

      setTotalAmount('');

      setExpenseTypeCode('');

      load();

    } catch (err) {

      showApiError(err, 'Failed to create expense');

    } finally {

      setCreating(false);

    }

  };



  const approveClaim = async (id: number) => {

    try {

      await hrApi.approveExpense(id);

      notify.success('Expense approved');

      load();

    } catch (err) {

      showApiError(err, 'Expense approval failed');

    }

  };



  const columns: Column<HrExpenseClaim>[] = [

    { key: 'id', header: '#' },

    { key: 'employeeId', header: 'Employee', render: (row) => getLabel(row.employeeId, true) },

    { key: 'claimDate', header: 'Date' },

    { key: 'expenseTypeCode', header: 'Type' },

    { key: 'totalAmount', header: 'Amount' },

    { key: 'statusCode', header: 'Status' },

    {
      key: 'actions',
      header: 'Actions',
      render: (row) =>
        row.statusCode === 'PENDING' || row.statusCode === 'SUBMITTED' || row.statusCode === 'DRAFT' ? (
          <TableRowActions>
            <Button variant="secondary" size="sm" onClick={() => approveClaim(row.id)}>
              Approve
            </Button>
          </TableRowActions>
        ) : null,
    },

  ];



  return (

    <div>

      <PageHeader

        title="Expense claims"

        description="Reimbursements with approval workflow and finance integration"

        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'HR', to: '/hr' }, { label: 'Claims' }]}

      />

      <div className="mb-6 rounded-lg border border-slate-200 p-4 dark:border-slate-700">

        <h3 className="mb-2 text-sm font-medium">New expense claim</h3>

        <FormGrid columns={4} className="items-end">

          <EmployeeSelect value={employeeId} onChange={setEmployeeId} hrScoped />

          <ExpenseTypeSelect value={expenseTypeCode} onChange={setExpenseTypeCode} required />

          <DatePicker label="Claim date" value={claimDate} onChange={setClaimDate} />

          <Input

            label="Amount"

            type="number"

            value={totalAmount}

            onChange={(e) => setTotalAmount(e.target.value)}

          />

        </FormGrid>

        <FormActions
          primaryLabel="Create claim"
          primaryLoading={creating}
          onPrimary={() => void createClaim()}
          onReset={() => {
            setEmployeeId('');
            setClaimDate('');
            setTotalAmount('');
            setExpenseTypeCode('');
          }}
        />

      </div>

      <DataTable

        columns={columns}

        data={paginateClientList(data, pagination.page, pagination.size)}

        loading={loading}

        keyField="id"

        {...dataTablePageProps(pagination)}

      />

    </div>

  );

}


